import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Plus, Search, AlertCircle, Clock, CheckCircle, XCircle, Inbox, Building2, Download, Paperclip, FileText, ChevronUp, ChevronDown, ChevronsUpDown, Flag } from 'lucide-react'
import { io, Socket } from 'socket.io-client'
import { useAuth } from '../hooks/useAuth'
import RichTextEditor from '../components/RichTextEditor'

// ─── Types ────────────────────────────────────────────────────────────────────

export type TicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
export type TicketStatus   = 'OPEN' | 'ASSIGNED' | 'IN_PROGRESS' | 'PENDING' | 'RESOLVED' | 'CLOSED' | 'CANCELLED'
export type TicketType     = 'INCIDENT' | 'SERVICE_REQUEST' | 'PROBLEM' | 'CHANGE'

export const PRIORITY_CFG: Record<TicketPriority, { label: string; color: string; dot: string }> = {
  CRITICAL: { label: 'Critical', color: 'bg-red-50 text-red-800 border border-red-200',      dot: 'bg-red-600' },
  HIGH:     { label: 'High',     color: 'bg-orange-50 text-orange-800 border border-orange-200', dot: 'bg-orange-500' },
  MEDIUM:   { label: 'Medium',   color: 'bg-amber-50 text-amber-800 border border-amber-200', dot: 'bg-amber-500' },
  LOW:      { label: 'Low',      color: 'bg-slate-100 text-slate-600 border border-slate-200', dot: 'bg-slate-400' },
}

export const STATUS_CFG: Record<TicketStatus, { label: string; color: string }> = {
  OPEN:        { label: 'Open',        color: 'bg-blue-50 text-blue-800 border border-blue-200' },
  ASSIGNED:    { label: 'Assigned',    color: 'bg-indigo-50 text-indigo-800 border border-indigo-200' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-violet-50 text-violet-800 border border-violet-200' },
  PENDING:     { label: 'On Hold',     color: 'bg-amber-50 text-amber-800 border border-amber-200' },
  RESOLVED:    { label: 'Resolved',    color: 'bg-emerald-50 text-emerald-800 border border-emerald-200' },
  CLOSED:      { label: 'Closed',      color: 'bg-slate-100 text-slate-500 border border-slate-200' },
  CANCELLED:   { label: 'Cancelled',   color: 'bg-red-50 text-red-500 border border-red-100' },
}

export const TYPE_CFG: Record<TicketType, { label: string; color: string }> = {
  INCIDENT:        { label: 'Incident',        color: 'bg-red-50 text-red-700 border border-red-100' },
  SERVICE_REQUEST: { label: 'Service Request', color: 'bg-blue-50 text-blue-700 border border-blue-100' },
  PROBLEM:         { label: 'Problem',         color: 'bg-orange-50 text-orange-700 border border-orange-100' },
  CHANGE:          { label: 'Change',          color: 'bg-teal-50 text-teal-700 border border-teal-100' },
}

// ─── SLA Countdown ───────────────────────────────────────────────────────────

export function SLABadge({ dueDate, status }: { dueDate?: string | null; status: TicketStatus }) {
  if (!dueDate || status === 'CLOSED' || status === 'CANCELLED' || status === 'RESOLVED') return null
  const remaining = new Date(dueDate).getTime() - Date.now()
  const totalMs   = new Date(dueDate).getTime()
  const isOverdue = remaining < 0

  if (isOverdue) {
    const hrs = Math.abs(Math.floor(remaining / 3600000))
    return (
      <span title={`เกินกำหนดแก้ไข ${hrs} ชม.`}
        className="text-[10px] font-medium text-red-700 bg-red-50 px-1 py-0.5 rounded border border-red-200 flex items-center gap-0.5 whitespace-nowrap leading-none">
        <Clock size={9} className="flex-shrink-0"/>{hrs}ชม.
      </span>
    )
  }
  const hrs  = Math.floor(remaining / 3600000)
  const mins = Math.floor((remaining % 3600000) / 60000)
  const pct  = Math.max(0, Math.min(100, (remaining / (totalMs - Date.now() + remaining)) * 100))
  const color = pct > 50 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : pct > 25 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200'
  return (
    <span title={`ครบกำหนดแก้ไขใน ${hrs > 0 ? `${hrs} ชม. ${mins} น.` : `${mins} น.`}`}
      className={`text-[10px] font-medium px-1 py-0.5 rounded border flex items-center gap-0.5 whitespace-nowrap leading-none ${color}`}>
      <Clock size={9} className="flex-shrink-0"/>
      {hrs > 0 ? `${hrs}ชม.${mins}น.` : `${mins}น.`}
    </span>
  )
}

// Response SLA: time from ticket creation until an assignee is set. Disappears
// once the ticket has an assignee — that's the moment "response" happened.
export function ResponseSLABadge({ responseDueDate, hasAssignee }: { responseDueDate?: string | null; hasAssignee: boolean }) {
  if (hasAssignee || !responseDueDate) return null
  const remaining = new Date(responseDueDate).getTime() - Date.now()
  const isOverdue = remaining < 0

  if (isOverdue) {
    const hrs = Math.abs(Math.floor(remaining / 3600000))
    return (
      <span title={`เกินกำหนดตอบสนอง ${hrs} ชม.`}
        className="text-[10px] font-medium text-red-700 bg-red-50 px-1 py-0.5 rounded border border-red-200 flex items-center gap-0.5 whitespace-nowrap leading-none">
        <Flag size={9} className="flex-shrink-0"/>{hrs}ชม.
      </span>
    )
  }
  const hrs  = Math.floor(remaining / 3600000)
  const mins = Math.floor((remaining % 3600000) / 60000)
  return (
    <span title={`ต้องมอบหมายภายใน ${hrs > 0 ? `${hrs} ชม. ${mins} น.` : `${mins} น.`}`}
      className="text-[10px] font-medium px-1 py-0.5 rounded border flex items-center gap-0.5 whitespace-nowrap leading-none text-indigo-700 bg-indigo-50 border-indigo-200">
      <Flag size={9} className="flex-shrink-0"/>
      {hrs > 0 ? `${hrs}ชม.${mins}น.` : `${mins}น.`}
    </span>
  )
}

// ─── UserSearchSelect ─────────────────────────────────────────────────────────

export function UserSearchSelect({ value, onChange, placeholder, members, label, disabled }: {
  value: string; onChange: (v: string) => void; placeholder: string; members: any[]; label?: string; disabled?: boolean
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen]     = useState(false)
  const [rect, setRect]     = useState<DOMRect | null>(null)
  const btnRef              = useRef<HTMLButtonElement>(null)
  const selected = members.find((m: any) => m.id === value)

  const filtered = members.filter((m: any) =>
    !search ||
    (m.displayName || m.username).toLowerCase().includes(search.toLowerCase()) ||
    m.username.toLowerCase().includes(search.toLowerCase()) ||
    m.company?.name?.toLowerCase().includes(search.toLowerCase())
  )

  const handleOpen = () => {
    if (disabled) return
    if (btnRef.current) setRect(btnRef.current.getBoundingClientRect())
    setOpen(o => !o)
  }

  return (
    <div className="relative">
      {label && <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>}
      <button ref={btnRef} type="button" onClick={handleOpen} disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2 ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}>
        {selected ? (
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
              {(selected.displayName || selected.username)[0].toUpperCase()}
            </span>
            <span className="truncate font-medium text-gray-800">{selected.displayName || selected.username}</span>
            {selected.company && <span className="text-gray-400 text-xs flex-shrink-0">({selected.company.name})</span>}
          </span>
        ) : <span className="text-gray-400 flex-1">{placeholder}</span>}
        {!disabled && <span className="text-gray-400 text-xs flex-shrink-0">▾</span>}
      </button>

      {open && rect && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 flex flex-col"
            style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}>
            <div className="p-2 border-b border-gray-100">
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="ค้นหาชื่อ, username..."/>
            </div>
            <div className="overflow-y-auto flex-1">
              <button type="button" onClick={() => { onChange(''); setOpen(false); setSearch('') }}
                className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50">— ไม่ระบุ —</button>
              {filtered.map((m: any) => (
                <button key={m.id} type="button"
                  onClick={() => { onChange(m.id); setOpen(false); setSearch('') }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 ${value === m.id ? 'bg-blue-50' : ''}`}>
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(m.displayName || m.username)[0].toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-gray-800">{m.displayName || m.username}</span>
                    <span className="text-gray-400 ml-1 text-xs">@{m.username}</span>
                  </span>
                  {m.company && <span className="text-gray-400 text-xs truncate max-w-[6rem]">{m.company.name}</span>}
                </button>
              ))}
              {filtered.length === 0 && <p className="px-3 py-4 text-sm text-gray-400 text-center">ไม่พบผู้ใช้</p>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── AssetSearchSelect ────────────────────────────────────────────────────────

export function AssetSearchSelect({ value, onChange, label }: {
  value: string; onChange: (id: string, asset?: any) => void; label?: string
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen]     = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const [rect, setRect]     = useState<DOMRect | null>(null)
  const btnRef              = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!value) { setSelected(null); return }
    if (selected?.id === value) return
    api.get(`/assets/${value}`).then(r => setSelected(r.data)).catch(() => setSelected(null))
  }, [value])

  const { data: results = [] } = useQuery<any[]>({
    queryKey: ['asset-search', search],
    queryFn: () => api.get('/assets', { params: { search, limit: 20 } }).then(r => r.data?.assets || r.data || []),
    enabled: open && search.length >= 1,
  })

  const handleOpen = () => {
    if (btnRef.current) setRect(btnRef.current.getBoundingClientRect())
    setOpen(o => !o)
  }

  return (
    <div className="relative">
      {label && <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>}
      <button ref={btnRef} type="button" onClick={handleOpen}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2">
        {selected ? (
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <span className="font-mono text-blue-700 text-xs flex-shrink-0">{selected.assetCode}</span>
            <span className="truncate text-gray-800">{selected.name}</span>
          </span>
        ) : <span className="text-gray-400 flex-1">ค้นหา Asset...</span>}
        <span className="text-gray-400 text-xs flex-shrink-0">▾</span>
      </button>

      {open && rect && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 flex flex-col"
            style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}>
            <div className="p-2 border-b border-gray-100">
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="พิมพ์รหัสหรือชื่อ Asset..."/>
            </div>
            <div className="overflow-y-auto flex-1">
              <button type="button" onClick={() => { onChange(''); setSelected(null); setOpen(false); setSearch('') }}
                className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50">— ไม่ระบุ —</button>
              {results.map((a: any) => (
                <button key={a.id} type="button"
                  onClick={() => { onChange(a.id, a); setSelected(a); setOpen(false); setSearch('') }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2 ${value === a.id ? 'bg-blue-50' : ''}`}>
                  <span className="font-mono text-blue-700 text-xs w-20 flex-shrink-0">{a.assetCode}</span>
                  <span className="flex-1 min-w-0 truncate text-gray-800">{a.name}</span>
                  <span className="text-gray-400 text-xs flex-shrink-0">{a.brand} {a.model}</span>
                </button>
              ))}
              {search.length >= 1 && results.length === 0 && <p className="px-3 py-4 text-sm text-gray-400 text-center">ไม่พบ Asset</p>}
              {search.length === 0 && <p className="px-3 py-3 text-xs text-gray-400 text-center">พิมพ์เพื่อค้นหา</p>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── NewTicketModal ───────────────────────────────────────────────────────────

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { user } = useAuth()
  const isViewer = user?.role === 'VIEWER'
  const [form, setForm] = useState({ title: '', description: '', type: 'INCIDENT', priority: 'MEDIUM', categoryId: '', subcategoryId: '', itemId: '', requesterId: user?.id || '', companyId: user?.companyId || '', assetId: '' })
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const MAX_FILE_MB = 20
  const addFiles = (list: FileList | null) => {
    if (!list?.length) return
    const picked = Array.from(list)
    const tooBig = picked.find(f => f.size > MAX_FILE_MB * 1024 * 1024)
    if (tooBig) { setError(`ไฟล์ "${tooBig.name}" ใหญ่เกิน ${MAX_FILE_MB} MB`); return }
    setError(null)
    setFiles(prev => [...prev, ...picked].slice(0, 10))
  }
  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx))

  const { data: categoryTree = [] } = useQuery<any[]>({ queryKey: ['ticket-categories'], queryFn: () => api.get('/tickets/categories').then(r => r.data) })
  const { data: members = [] }      = useQuery<any[]>({ queryKey: ['users-members'],     queryFn: () => api.get('/users/members').then(r => r.data) })
  const { data: companies = [] }    = useQuery<any[]>({ queryKey: ['companies'],         queryFn: () => api.get('/masterdata/companies').then(r => r.data) })

  const selectedCat    = categoryTree.find((c: any) => c.id === form.categoryId)
  const subcategories: any[] = selectedCat?.children || []
  const selectedSub    = subcategories.find((s: any) => s.id === form.subcategoryId)
  const items: any[]   = selectedSub?.children || []

  const handleCategoryChange = (id: string) => setForm(f => ({ ...f, categoryId: id, subcategoryId: '', itemId: '' }))
  const handleSubcatChange   = (id: string) => setForm(f => ({ ...f, subcategoryId: id, itemId: '' }))

  const submit = async () => {
    if (!form.title.trim()) { setError('กรุณากรอกหัวข้อ'); return }
    setLoading(true); setError(null)
    try {
      const payload: any = { ...form }
      if (!payload.requesterId)    delete payload.requesterId
      if (!payload.categoryId)     delete payload.categoryId
      if (!payload.subcategoryId)  delete payload.subcategoryId
      if (!payload.itemId)         delete payload.itemId
      if (!payload.companyId)      delete payload.companyId
      if (!payload.assetId)        delete payload.assetId
      const { data: created } = await api.post('/tickets', payload)
      if (files.length && created?.id) {
        const fd = new FormData()
        files.forEach(f => fd.append('files', f))
        await api.post(`/tickets/${created.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      onCreated(); onClose()
    } catch (e: any) { setError(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const sel = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">สร้าง Ticket ใหม่</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><XCircle size={18}/></button>
        </div>
        <div className="p-5 space-y-4 max-h-[72vh] overflow-y-auto">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">หัวข้อ *</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="อธิบายปัญหาหรือคำขอโดยย่อ"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">ประเภท</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={sel}>
                <option value="INCIDENT">Incident (ปัญหา)</option>
                <option value="SERVICE_REQUEST">Service Request (คำขอ)</option>
                <option value="CHANGE">Change (การเปลี่ยนแปลง)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">ความสำคัญ</label>
              <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className={sel}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
          </div>

          {/* Cascading Category → Subcategory → Item */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600 block">หมวดหมู่</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">Category</p>
                <select value={form.categoryId} onChange={e => handleCategoryChange(e.target.value)} className={sel}>
                  <option value="">— ไม่ระบุ —</option>
                  {categoryTree.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Subcategory</p>
                <select value={form.subcategoryId} onChange={e => handleSubcatChange(e.target.value)} className={sel} disabled={!form.categoryId || subcategories.length === 0}>
                  <option value="">— ไม่ระบุ —</option>
                  {subcategories.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Item</p>
                <select value={form.itemId} onChange={e => setForm(f => ({...f, itemId: e.target.value}))} className={sel} disabled={!form.subcategoryId || items.length === 0}>
                  <option value="">— ไม่ระบุ —</option>
                  {items.map((i: any) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">บริษัท</label>
            <select value={form.companyId} onChange={e => setForm(f => ({...f, companyId: e.target.value}))} className={sel} disabled={isViewer}>
              <option value="">— ไม่ระบุ —</option>
              {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <AssetSearchSelect
              label="Asset ที่เกี่ยวข้อง"
              value={form.assetId}
              onChange={id => setForm(f => ({...f, assetId: id}))}
            />
          </div>

          <div>
            <UserSearchSelect
              label="ผู้เปิด Ticket"
              value={form.requesterId}
              onChange={v => setForm(f => ({...f, requesterId: v}))}
              placeholder="ค้นหาผู้แจ้ง..."
              members={members}
              disabled={isViewer}
            />
            <p className="text-xs text-gray-400 mt-1">{isViewer ? 'คุณสามารถเปิด Ticket ในนามตัวเองเท่านั้น' : 'เว้นว่าง = ผู้ใช้ปัจจุบัน'}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">รายละเอียด</label>
            <RichTextEditor
              content={form.description}
              onChange={html => setForm(f => ({...f, description: html}))}
              placeholder="อธิบายรายละเอียดปัญหาหรือคำขอ... (แทรกรูปภาพหรือตารางได้)"
              uploadEndpoint="/tickets/upload-image"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">ไฟล์แนบ</label>
            <div
              className="border border-dashed border-gray-300 rounded-lg p-3"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files) }}
            >
              {files.length === 0 ? (
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-full py-3 flex flex-col items-center gap-1 text-gray-400 hover:text-blue-600">
                  <Paperclip size={18}/>
                  <span className="text-xs">คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวาง — PDF, Excel, Word, รูปภาพ ฯลฯ (สูงสุด {MAX_FILE_MB} MB/ไฟล์)</span>
                </button>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-lg">
                      <FileText size={13} className="text-gray-400"/>
                      <span className="max-w-40 truncate text-gray-700">{f.name}</span>
                      <span className="text-gray-400">({Math.round(f.size / 1024)}KB)</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-gray-300 hover:text-red-500 ml-0.5"><XCircle size={12}/></button>
                    </div>
                  ))}
                  {files.length < 10 && (
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 border border-dashed border-blue-200 px-2.5 py-1.5 rounded-lg">
                      <Plus size={11}/> เพิ่มไฟล์
                    </button>
                  )}
                </div>
              )}
              <input ref={fileRef} type="file" multiple className="hidden"
                accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.txt,.zip,.rar,.7z,image/*"
                onChange={e => { addFiles(e.target.files); e.target.value = '' }}/>
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">ยกเลิก</button>
          <button onClick={submit} disabled={loading}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'กำลังสร้าง...' : 'สร้าง Ticket'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar() {
  const { data } = useQuery({ queryKey: ['ticket-stats'], queryFn: () => api.get('/tickets/stats').then(r => r.data) })
  if (!data) return null

  const open       = data.byStatus?.find((s: any) => s.status === 'OPEN')?._count._all || 0
  const assigned   = data.byStatus?.find((s: any) => s.status === 'ASSIGNED')?._count._all || 0
  const inProgress = data.byStatus?.find((s: any) => s.status === 'IN_PROGRESS')?._count._all || 0
  const pending    = data.byStatus?.find((s: any) => s.status === 'PENDING')?._count._all || 0
  const resolved   = data.byStatus?.find((s: any) => s.status === 'RESOLVED')?._count._all || 0

  const avgHrs = data.avgResolutionMinutes ? (data.avgResolutionMinutes / 60).toFixed(1) : '—'

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
      {[
        { label: 'เปิด',             value: open,                      icon: Inbox,        accent: 'border-l-blue-600',   iconColor: 'text-blue-700' },
        { label: 'มอบหมายแล้ว',     value: assigned,                  icon: CheckCircle,  accent: 'border-l-indigo-600', iconColor: 'text-indigo-700' },
        { label: 'กำลังดำเนินการ',  value: inProgress,                icon: AlertCircle,  accent: 'border-l-violet-600', iconColor: 'text-violet-700' },
        { label: 'รอดำเนินการ',     value: pending,                   icon: Clock,        accent: 'border-l-amber-600',  iconColor: 'text-amber-700' },
        { label: 'แก้ไขแล้ว',       value: resolved,                  icon: CheckCircle,  accent: 'border-l-emerald-600', iconColor: 'text-emerald-700' },
        { label: 'เกิน SLA',         value: data.overdue,              icon: AlertCircle,  accent: 'border-l-red-600',    iconColor: 'text-red-700' },
        { label: 'SLA Breach (เดือนนี้)', value: `${data.slaBreachRate}%`, icon: XCircle,      accent: 'border-l-orange-600', iconColor: 'text-orange-700' },
      ].map(({ label, value, icon: Icon, accent, iconColor }) => (
        <div key={label} className={`bg-white rounded-md border border-gray-200 border-l-4 ${accent} px-3 py-2 flex items-center justify-between gap-2`}>
          <div className="min-w-0">
            <p className="text-lg font-semibold text-gray-900 tabular-nums leading-tight">{value}</p>
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide truncate">{label}</p>
          </div>
          <Icon size={15} className={`${iconColor} flex-shrink-0 opacity-70`}/>
        </div>
      ))}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TicketsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isViewer = user?.role === 'VIEWER'
  const [activeTab, setActiveTab]   = useState<'all' | 'open' | 'onhold' | 'mine' | 'unassigned'>('open')
  const [search, setSearch]         = useState('')
  const [filterPriority, setFP]     = useState('')
  const [filterType, setFT]         = useState('')
  const [filterStatus, setFS]       = useState('')
  const [filterCategory, setFC]     = useState('')
  const [page, setPage]             = useState(1)
  const [sortBy, setSortBy]         = useState('')
  const [sortOrder, setSortOrder]   = useState<'asc' | 'desc'>('desc')
  const [showModal, setShowModal]   = useState(false)
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)

  const { data: categoryTree = [] } = useQuery<any[]>({ queryKey: ['ticket-categories'], queryFn: () => api.get('/tickets/categories').then(r => r.data) })

  const params: any = {
    page, limit: 25,
    ...(sortBy && { sortBy, sortOrder }),
    ...(search         && { search }),
    ...(filterPriority && { priority: filterPriority }),
    ...(filterType     && { type: filterType }),
    ...(filterCategory && { categoryId: filterCategory }),
    ...(activeTab === 'open'       && !filterStatus && { statusNotIn: 'PENDING,RESOLVED,CLOSED,CANCELLED' }),
    ...(activeTab === 'open'       && filterStatus  && { status: filterStatus }),
    ...(activeTab === 'onhold'     && !filterStatus && { status: 'PENDING' }),
    ...(activeTab === 'onhold'     && filterStatus  && { status: filterStatus }),
    ...(activeTab === 'mine'       && { mine: 'true',      ...(filterStatus && { status: filterStatus }) }),
    ...(activeTab === 'unassigned' && { unassigned: 'true', ...(filterStatus && { status: filterStatus }) }),
    ...(activeTab === 'all'        && filterStatus  && { status: filterStatus }),
  }

  const { data, refetch } = useQuery({
    queryKey: ['tickets', params],
    queryFn: () => api.get('/tickets', { params }).then(r => r.data),
  })

  const tickets: any[] = data?.tickets || []

  const handleSort = (col: string) => {
    if (sortBy === col) setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'))
    else { setSortBy(col); setSortOrder('asc') }
    setPage(1)
  }
  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <ChevronsUpDown size={12} className="text-gray-300"/>
    return sortOrder === 'asc'
      ? <ChevronUp size={12} className="text-blue-500"/>
      : <ChevronDown size={12} className="text-blue-500"/>
  }

  // Socket.io real-time updates
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const companyId = user?.companyId || ''
    const apiBase = (import.meta as any).env?.VITE_API_URL?.replace('/api', '') || ''
    const socket: Socket = io(apiBase || window.location.origin, {
      path: '/socket.io',
      query: { companyId },
      transports: ['websocket', 'polling'],
    })
    socket.on('ticket:created', () => refetch())
    socket.on('tickets:refresh', () => refetch())
    socket.on('sla:breach', () => refetch())
    return () => { socket.disconnect() }
  }, [])

  const handleExportCSV = async () => {
    const exportParams = { ...params, page: 1, limit: 1000 }
    const res = await api.get('/tickets', { params: exportParams })
    const rows: any[] = res.data?.tickets || []
    if (!rows.length) return
    const headers = ['#','หัวข้อ','ประเภท','Priority','สถานะ','หมวดหมู่','ผู้แจ้ง','ผู้รับผิดชอบ','บริษัท','Asset','Due Date','วันที่สร้าง']
    const csvRows = rows.map((t: any) => [
      `#${String(t.ticketNumber).padStart(4,'0')}`,
      t.title,
      TYPE_CFG[t.type as TicketType]?.label || t.type,
      PRIORITY_CFG[t.priority as TicketPriority]?.label || t.priority,
      STATUS_CFG[t.status as TicketStatus]?.label || t.status,
      [t.category?.name, t.subcategory?.name, t.item?.name].filter(Boolean).join(' › '),
      t.requester?.displayName || t.requester?.username || '',
      t.assignee?.displayName || t.assignee?.username || '',
      t.company?.name || '',
      t.asset?.assetCode || '',
      t.dueDate ? new Date(t.dueDate).toLocaleString('th-TH-u-ca-gregory') : '',
      new Date(t.createdAt).toLocaleString('th-TH-u-ca-gregory'),
    ].map(v => `"${String(v).replace(/"/g,'""')}"`)
    )
    const csv = [headers.map(h=>`"${h}"`).join(','), ...csvRows.map(r=>r.join(','))].join('\n')
    const blob = new Blob(['﻿'+csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `tickets-${new Date().toISOString().slice(0,10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === tickets.length && tickets.length > 0) {
      setSelected(new Set())
    } else {
      setSelected(new Set(tickets.map((t:any) => t.id)))
    }
  }

  const handleBulkStatus = async (status: string) => {
    if (!selected.size) return
    setBulkLoading(true)
    try {
      await Promise.all([...selected].map(id => api.put(`/tickets/${id}`, { status })))
      setSelected(new Set())
      refetch()
    } finally { setBulkLoading(false) }
  }

  const handleBulkDelete = async () => {
    if (!selected.size) return
    if (!confirm(`ลบ ${selected.size} Ticket ที่เลือก?`)) return
    setBulkLoading(true)
    try {
      await Promise.all([...selected].map(id => api.delete(`/tickets/${id}`)))
      setSelected(new Set())
      refetch()
    } finally { setBulkLoading(false) }
  }

  const tabBtnCls = (t: string) =>
    `px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors ${activeTab === t ? 'border-b-2 border-blue-700 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`

  return (
    <div className="h-full flex flex-col p-4 gap-3 overflow-hidden">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 tracking-tight">ServiceDesk</h1>
          <p className="text-xs text-gray-500 mt-0.5">จัดการ Tickets และ Service Requests</p>
        </div>
        <div className="flex items-center gap-2">
          {!isViewer && (
            <button onClick={handleExportCSV}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Download size={15}/> Export CSV
            </button>
          )}
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-md text-sm font-medium hover:bg-blue-800">
            <Plus size={16}/> สร้าง Ticket
          </button>
        </div>
      </div>

      {!isViewer && <StatsBar/>}

      {/* Tabs */}
      <div className="flex-1 min-h-0 bg-white rounded-md border border-gray-200 overflow-hidden flex flex-col">
        <div className="flex gap-1 border-b border-gray-200 px-4 bg-gray-50/60">
          <button onClick={() => { setActiveTab('all'); setPage(1) }}       className={tabBtnCls('all')}>ทั้งหมด</button>
          <button onClick={() => { setActiveTab('open'); setPage(1) }}      className={tabBtnCls('open')}>เปิดอยู่</button>
          <button onClick={() => { setActiveTab('onhold'); setPage(1) }}    className={tabBtnCls('onhold')}>On Hold</button>
          <button onClick={() => { setActiveTab('mine'); setPage(1) }}      className={tabBtnCls('mine')}>ของฉัน</button>
          <button onClick={() => { setActiveTab('unassigned'); setPage(1) }} className={tabBtnCls('unassigned')}>ยังไม่ได้มอบหมาย</button>
        </div>

        {/* Filters */}
        <div className="p-4 flex flex-wrap gap-2 border-b border-gray-200 bg-white">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
              placeholder="ค้นหาหัวข้อ หรือชื่อผู้เปิด..."/>
          </div>
          <select value={filterPriority} onChange={e => { setFP(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600">
            <option value="">ทุก Priority</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <select value={filterType} onChange={e => { setFT(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600">
            <option value="">ทุกประเภท</option>
            <option value="INCIDENT">Incident</option>
            <option value="SERVICE_REQUEST">Service Request</option>
            <option value="CHANGE">Change</option>
          </select>
          <select value={filterStatus} onChange={e => { setFS(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600">
            <option value="">ทุกสถานะ</option>
            <option value="OPEN">Open</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="PENDING">On Hold</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <select value={filterCategory} onChange={e => { setFC(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600">
            <option value="">ทุกหมวดหมู่</option>
            {categoryTree.map((c: any) => (
              <optgroup key={c.id} label={c.name}>
                <option value={c.id}>{c.name} (ทั้งหมด)</option>
                {(c.children || []).map((s: any) => (
                  <option key={s.id} value={s.id}>  {s.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {(filterPriority || filterType || filterStatus || filterCategory || search) && (
            <button onClick={() => { setSearch(''); setFP(''); setFT(''); setFS(''); setFC(''); setPage(1) }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 border border-gray-300 rounded-md hover:border-red-300">
              <XCircle size={13}/> ล้าง
            </button>
          )}
          <span className="ml-auto self-center text-sm text-gray-500 whitespace-nowrap">{data?.total ?? 0} รายการ</span>
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (
          <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-blue-800">เลือก {selected.size} รายการ</span>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => handleBulkStatus('IN_PROGRESS')} disabled={bulkLoading}
                className="px-3 py-1 text-xs font-medium bg-white text-violet-700 border border-violet-200 rounded hover:bg-violet-50 disabled:opacity-50">
                → In Progress
              </button>
              <button onClick={() => handleBulkStatus('RESOLVED')} disabled={bulkLoading}
                className="px-3 py-1 text-xs font-medium bg-white text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-50 disabled:opacity-50">
                → Resolved
              </button>
              <button onClick={() => handleBulkStatus('CLOSED')} disabled={bulkLoading}
                className="px-3 py-1 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                → Closed
              </button>
              <button onClick={() => handleBulkStatus('CANCELLED')} disabled={bulkLoading}
                className="px-3 py-1 text-xs font-medium bg-white text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50">
                → Cancelled
              </button>
              <button onClick={handleBulkDelete} disabled={bulkLoading}
                className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                ลบ
              </button>
              <button onClick={() => setSelected(new Set())} className="px-3 py-1 text-xs text-gray-400 hover:text-gray-600">
                ยกเลิกการเลือก
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input type="checkbox" checked={selected.size === tickets.length && tickets.length > 0} onChange={toggleAll}
                    className="rounded border-gray-300 text-blue-700 cursor-pointer"/>
                </th>
                {[
                  { label: '#',              col: 'ticketNumber', cls: 'w-20' },
                  { label: 'หัวข้อ',          col: 'title',        cls: '' },
                  { label: 'Category',        col: 'category',     cls: 'w-40' },
                  { label: 'ประเภท',          col: 'type',         cls: 'w-28' },
                  { label: 'ความสำคัญ',       col: 'priority',     cls: 'w-24' },
                  { label: 'สถานะ',           col: 'status',       cls: 'w-32' },
                  { label: 'SLA',             col: '',             cls: 'w-36' },
                  { label: 'บริษัท',          col: 'company',      cls: 'w-28' },
                  { label: 'ผู้รับผิดชอบ',    col: 'assignee',     cls: 'w-28' },
                  { label: 'ผู้เปิด',         col: 'requester',    cls: 'w-28' },
                  { label: 'Create DateTime', col: 'createdAt',    cls: 'w-32' },
                  { label: 'Due DateTime',    col: 'dueDate',      cls: 'w-32' },
                ].map(({ label, col, cls }) => (
                  <th key={label}
                    onClick={() => col && handleSort(col)}
                    className={`px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide select-none ${cls} ${col ? 'cursor-pointer hover:bg-gray-200/60 hover:text-gray-800' : ''}`}>
                    <div className="flex items-center gap-1">
                      {label}
                      {col && <SortIcon col={col}/>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-12 text-center text-gray-400">
                  <Inbox size={32} className="mx-auto mb-2 text-gray-300"/>
                  ไม่พบ Ticket
                </td></tr>
              ) : tickets.map((t: any) => (
                <tr key={t.id}
                  className={`hover:bg-blue-50 transition-colors ${selected.has(t.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-1.5 align-middle cursor-default" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)}
                      className="rounded border-gray-300 text-blue-700 cursor-pointer"/>
                  </td>
                  <td className="px-4 py-1.5 align-middle font-mono text-gray-500 text-xs cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    #{String(t.ticketNumber).padStart(4, '0')}
                  </td>
                  <td className="px-4 py-1.5 align-middle cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <p className="font-medium text-gray-900 line-clamp-1">{t.title}</p>
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs text-gray-500 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {(t.category || t.subcategory || t.item)
                      ? [t.category?.name, t.subcategory?.name, t.item?.name].filter(Boolean).join(' › ')
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-1.5 align-middle cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${TYPE_CFG[t.type as TicketType]?.color}`}>
                      {TYPE_CFG[t.type as TicketType]?.label}
                    </span>
                  </td>
                  <td className="px-4 py-1.5 align-middle cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium flex items-center gap-1 w-fit whitespace-nowrap ${PRIORITY_CFG[t.priority as TicketPriority]?.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_CFG[t.priority as TicketPriority]?.dot}`}/>
                      {PRIORITY_CFG[t.priority as TicketPriority]?.label}
                    </span>
                  </td>
                  <td className="px-4 py-1.5 align-middle cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${STATUS_CFG[t.status as TicketStatus]?.color}`}>
                      {STATUS_CFG[t.status as TicketStatus]?.label}
                    </span>
                  </td>
                  <td className="px-4 py-1.5 align-middle cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <div className="flex flex-row flex-nowrap gap-1 items-center">
                      <ResponseSLABadge responseDueDate={t.responseDueDate} hasAssignee={!!t.assigneeId}/>
                      <SLABadge dueDate={t.dueDate} status={t.status}/>
                    </div>
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs text-gray-600 cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {t.company ? (
                      <span className="flex items-center gap-1 text-gray-600 whitespace-nowrap">
                        <Building2 size={11} className="text-gray-400 flex-shrink-0"/>{t.company.name}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs text-gray-600 cursor-pointer whitespace-nowrap" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {t.assignee?.displayName || t.assignee?.username || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs text-gray-600 cursor-pointer whitespace-nowrap" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {t.requester?.displayName || t.requester?.username || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs text-gray-400 cursor-pointer whitespace-nowrap" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {new Date(t.createdAt).toLocaleDateString('th-TH-u-ca-gregory', { day:'2-digit', month:'2-digit', year:'numeric' })}
                    {' '}<span className="text-gray-300">{new Date(t.createdAt).toLocaleTimeString('th-TH', { hour:'2-digit', minute:'2-digit' })}</span>
                  </td>
                  <td className="px-4 py-1.5 align-middle text-xs cursor-pointer whitespace-nowrap" onClick={() => navigate(`/tickets/${t.id}`)}>
                    {t.dueDate
                      ? <span className={new Date(t.dueDate) < new Date() && !['RESOLVED','CLOSED','CANCELLED'].includes(t.status) ? 'text-red-500 font-medium' : 'text-gray-400'}>
                          {new Date(t.dueDate).toLocaleDateString('th-TH-u-ca-gregory', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          {' '}<span className="opacity-70">{new Date(t.dueDate).toLocaleTimeString('th-TH', { hour:'2-digit', minute:'2-digit' })}</span>
                        </span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50/60">
            <p className="text-xs text-gray-500">หน้า {page} / {data.totalPages} · {data.total} รายการ</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40">← ก่อนหน้า</button>
              <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40">ถัดไป →</button>
            </div>
          </div>
        )}
      </div>

      {showModal && <NewTicketModal onClose={() => setShowModal(false)} onCreated={() => { refetch(); setShowModal(false) }}/>}
    </div>
  )
}
